import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import {
  getSnapshot,
  setNotifier,
  register,
  unregister,
  notify,
} from './globalSvgMaskRegistry';

/**
 * Renders all currently registered SVG masks in a single hidden SVG defs.
 * Mount once at app root so url(#id) resolves to a single definition per id.
 */
function GlobalSvgMasks() {
  const [masks, setMasks] = useState(getSnapshot);

  useEffect(() => {
    setNotifier(setMasks);
    return () => setNotifier(null);
  }, []);

  const entries = Array.from(masks.entries());

  if (entries.length === 0) {
    return null;
  }

  return (
    <svg
      aria-hidden="true"
      style={{
        position: 'absolute',
        width: 0,
        height: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
    >
      <defs>
        {entries.map(([id, content]) => (
          <mask id={id} key={id}>
            {content}
          </mask>
        ))}
      </defs>
    </svg>
  );
}

/**
 * Register a mask by id for the global defs. The last registration for a given
 * id is the one that renders. Unregisters on unmount (or when id/content change).
 *
 * @param {string} id - Mask id (use in mask={`url(#${id})`}).
 * @param {import('react').ReactNode} content - Children of the <mask> (e.g. <circle ... />).
 *
 * @example
 * useGlobalSvgMask('my-mask', <circle cx={0} cy={0} r={10} fill="white" />);
 * // Then elsewhere: <path mask="url(#my-mask)" ... />
 */
export function useGlobalSvgMask(id, content) {
  const ownerRef = useRef(Object.create(null));

  useLayoutEffect(() => {
    const cleanupRef = ownerRef.current;
    register(id, content, ownerRef.current);
    notify();
    return () => {
      unregister(id, cleanupRef);
      notify();
    };
  }, [id, content]);
}

export default GlobalSvgMasks;
